"use client";

import { Handshake } from "lucide-react";
import { PortalTooltip } from "modules/common/components/PortalTooltip";
import type { TradeItem } from "modules/tradeLists/model/tradeList";
import {
  spokenForQuantity,
  totalQuantity,
} from "modules/tradeLists/utils/tradeItemAvailability";
import { cn } from "utils/utils";

interface ActiveProposalBadgeProps {
  item: {
    quantity: TradeItem["quantity"] | number;
    spokenForQuantity?: number;
  };
  className?: string;
  badgeClassName?: string;
  /**
   * Owner-only navigation affordance. Omit this unless the caller has
   * authorized active-trade references for the grouped item; browse rows for
   * other users must keep the existing informational badge.
   */
  onOpenTrade?: () => void;
  activeTradeCount?: number;
}

export function ActiveProposalBadge({
  item,
  className,
  badgeClassName,
  onOpenTrade,
  activeTradeCount,
}: ActiveProposalBadgeProps) {
  const total = totalQuantity(item);
  const activeProposalQuantity = spokenForQuantity(item);

  if (activeProposalQuantity <= 0) return null;

  const isPartial = activeProposalQuantity < total;
  const label = isPartial
    ? `${activeProposalQuantity}/${total} in trade`
    : total > 1
      ? `${activeProposalQuantity} in trade`
      : "In trade";
  const statusDescription = isPartial
    ? `${activeProposalQuantity} of ${total} copies in trade`
    : total === 1
      ? "In trade"
      : `All ${total} copies in trade`;
  const actionDescription =
    (activeTradeCount ?? 0) > 1 ? "Choose trade" : "Open trade";
  const accessibleDescription = onOpenTrade
    ? `${statusDescription}. ${actionDescription}.`
    : statusDescription;
  const badgeClass = cn(
    "badge badge-warning badge-soft badge-sm gap-1 whitespace-nowrap font-semibold",
    onOpenTrade &&
      "focus-visible:outline-warning cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2",
    badgeClassName
  );
  const badgeContents = (
    <>
      <Handshake size={12} aria-hidden />
      {label}
    </>
  );

  return (
    <PortalTooltip
      className={className}
      placement="top"
      content={
        <span
          role="tooltip"
          className="bg-neutral text-neutral-content relative mb-2 block whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md"
        >
          {onOpenTrade ? actionDescription : statusDescription}
          <span
            aria-hidden
            className="bg-neutral absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45"
          />
        </span>
      }
    >
      {onOpenTrade ? (
        <button
          type="button"
          aria-label={accessibleDescription}
          className={badgeClass}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenTrade();
          }}
        >
          {badgeContents}
        </button>
      ) : (
        <span aria-label={accessibleDescription} className={badgeClass}>
          {badgeContents}
        </span>
      )}
    </PortalTooltip>
  );
}
